'use client';

import { useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Check, Copy, Download } from 'lucide-react';
import { EditorShell } from '@/components/datahunter/editor-shell';
import { useAdoptPrefilledValue } from '@/components/datahunter/use-adopt-prefilled-value';
import { renderMarkdown } from '@/lib/markdown';

/**
 * Редактор Markdown: текст слева, результат справа.
 *
 * Предпросмотр считается на каждое изменение. Это дешевле, чем кажется:
 * разбор строк линейный, а документы такого редактора измеряются
 * страницами, а не мегабайтами.
 */
export function MarkdownEditor({ title }: { title: string }) {
  const t = useTranslations('dh_editor');
  const [copied, setCopied] = useState(false);

  return (
    <EditorShell
      kind="markdown"
      title={title}
      toolbar={({ value }) => (
        <>
          <button
            disabled={!value}
            onClick={() => {
              void navigator.clipboard.writeText(value).then(() => {
                setCopied(true);
                setTimeout(() => {
                  setCopied(false);
                }, 2000);
              });
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-sm hover:border-[var(--color-accent-500)] transition-colors disabled:opacity-50"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? t('copied') : t('copy')}
          </button>

          <button
            disabled={!value}
            onClick={() => {
              const url = URL.createObjectURL(new Blob([value], { type: 'text/markdown' }));
              const link = document.createElement('a');
              link.href = url;
              link.download = 'document.md';
              link.click();
              URL.revokeObjectURL(url);
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-sm hover:border-[var(--color-accent-500)] transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            {t('download')}
          </button>
        </>
      )}
    >
      {({ value, onChange, readOnly }) => (
        <MarkdownArea value={value} onChange={onChange} readOnly={readOnly} />
      )}
    </EditorShell>
  );
}

function MarkdownArea({
  value,
  onChange,
  readOnly,
}: {
  value: string;
  onChange: (value: string) => void;
  readOnly: boolean;
}) {
  const t = useTranslations('dh_editor');
  const html = useMemo(() => renderMarkdown(value), [value]);
  const ref = useRef<HTMLTextAreaElement>(null);

  // Текст, набранный до гидратации, не должен пропасть.
  useAdoptPrefilledValue(ref, value, onChange);

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        readOnly={readOnly}
        spellCheck={false}
        placeholder={t('placeholder_markdown')}
        aria-label={t('placeholder_markdown')}
        className="w-full h-[480px] resize-y rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4 font-mono text-sm leading-relaxed outline-none focus:border-[var(--color-accent-500)] transition-colors"
      />

      <div
        aria-label={t('preview')}
        className="dh-preview h-[480px] overflow-y-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4 text-sm leading-relaxed"
        // Разметка построена нашим рендерером: входной текст экранируется
        // до разбора, теги пользователя остаются текстом, схемы ссылок
        // проверяются. См. lib/markdown.ts.
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}
